import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ListBulletIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { createDataset, testDatasetExternalApi } from '../../services/datasetsApi';
import useSessionContext from '../../hooks/useSessionContext';
import { Button, Input, TextArea, Select, SwitchToggle } from '../../ui/atoms';
import Banner from '../../components/Notifications/Banner';

const API_SOURCE_PRESETS = {
  ncbi: {
    datasetName: 'NCBI PubMed Dataset',
    functionName: 'search_ncbi_articles',
    description: 'Search NCBI PubMed Central for scientific articles.',
    url: 'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi',
    method: 'GET',
    parameters: [
      { name: 'db', value: 'pmc', description: 'Database name', required: true },
      { name: 'term', value: '', description: 'Search query', required: true },
      { name: 'retmax', value: '20', description: 'Max results', required: false },
      { name: 'retmode', value: 'json', description: 'Response mode', required: false }
    ]
  },
  sejm: {
    datasetName: 'Sejm ELI Dataset',
    functionName: 'sejm_eli_lookup',
    description: 'Query Polish legal acts and metadata from Sejm ELI API.',
    url: 'https://api.sejm.gov.pl/eli/acts/DU/2024',
    method: 'GET',
    parameters: []
  },
  saos: {
    datasetName: 'SAOS Judgments Dataset',
    functionName: 'saos_search_judgments',
    description: 'Search judgments from SAOS API.',
    url: 'https://www.saos.org.pl/api/search/judgments',
    method: 'GET',
    parameters: [
      { name: 'pageSize', value: '20', description: 'Page size', required: false },
      { name: 'pageNumber', value: '0', description: 'Page number', required: false }
    ]
  },
  krs: {
    datasetName: 'KRS Open API Dataset',
    functionName: 'krs_get_entity',
    description: 'Fetch KRS records from official KRS Open API.',
    url: 'https://api-krs.ms.gov.pl/api/krs/OdpisAktualny/0000028860?rejestr=P&format=json',
    method: 'GET',
    parameters: []
  },
  ceidg: {
    datasetName: 'CEIDG Dataset',
    functionName: 'ceidg_lookup_business',
    description: 'Lookup CEIDG entities (requires CEIDG integrator credentials).',
    url: 'https://dane.biznes.gov.pl',
    method: 'GET',
    parameters: []
  },
  vat: {
    datasetName: 'VAT White List Dataset',
    functionName: 'vat_whitelist_lookup',
    description: 'Verify VAT status from Polish White List API.',
    url: 'https://wl-api.mf.gov.pl/api/search/nip/5260250274?date=2024-01-15',
    method: 'GET',
    parameters: []
  },
  cellar: {
    datasetName: 'EUR-Lex Cellar Dataset',
    functionName: 'cellar_sparql_query',
    description: 'Query EU legal metadata from Cellar SPARQL endpoint.',
    url: 'https://publications.europa.eu/webapi/rdf/sparql',
    method: 'GET',
    parameters: [
      { name: 'query', value: '', description: 'SPARQL query', required: true }
    ]
  }
};

const CreateApiDataset = ({ presetId = null }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [datasetName, setDatasetName] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [visibilityTargets, setVisibilityTargets] = useState(['chat', 'articles']);
  const [activeTab, setActiveTab] = useState('params');
  const [testResponse, setTestResponse] = useState(null);
  const [testError, setTestError] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [prefillNotice, setPrefillNotice] = useState('');
  const [presetEnabled, setPresetEnabled] = useState(true);

  const [apiConfig, setApiConfig] = useState({
    functionName: '',
    description: '',
    url: '', // Combined baseUrl + endpoint
    method: 'GET',
    parameters: [],
    headers: [],
    auth: {
      type: 'none', // none, bearer, basic, api-key
      token: '',
      username: '',
      password: '',
      apiKey: '',
      apiKeyHeader: 'X-API-Key'
    },
    responseFormat: 'json'
  });

  const navigate = useNavigate();
  const { token, workspaceId: sessionWorkspaceId, refreshContext } = useSessionContext();
  const presetConfig = useMemo(
    () => (presetId && API_SOURCE_PRESETS[presetId] ? API_SOURCE_PRESETS[presetId] : null),
    [presetId]
  );
  const isPresetLocked = Boolean(presetConfig);

  React.useEffect(() => {
    const prefill = searchParams.get('prefill');
    const localPrefillMode = localStorage.getItem('wp_prefill_mode');
    const isWpPrivatePrefill = prefill === 'wp_private_search' || localPrefillMode === 'wp_workspace_connect';
    const isWpV2Prefill = prefill === 'wp_rest_v2_search';
    if (!isWpPrivatePrefill && !isWpV2Prefill) return;

    const wpEndpoint = searchParams.get('wp_endpoint') || localStorage.getItem('wp_prefill_endpoint') || '';
    const wpWorkspaceId = searchParams.get('wp_workspace_id') || searchParams.get('workspace_id') || '';

    setDatasetName((prev) => prev || 'WordPress AI Search');
    setApiConfig((prev) => ({
      ...prev,
      functionName: prev.functionName || 'wp_content_search',
      description: prev.description || (isWpV2Prefill
        ? 'Search WordPress content via WP REST API v2 search endpoint.'
        : 'Search WordPress content from private endpoint configured in WordPress plugin.'),
      url: prev.url || wpEndpoint,
      method: isWpV2Prefill ? 'GET' : 'POST'
    }));

    setPrefillNotice(
      wpWorkspaceId
        ? `Prefilled from WordPress plugin (workspace ${wpWorkspaceId}).`
        : 'Prefilled from WordPress plugin.'
    );

    // One-time consume local prefill values.
    if (localPrefillMode === 'wp_workspace_connect') {
      localStorage.removeItem('wp_prefill_mode');
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (!presetConfig) return;
    const preset = presetConfig;

    setDatasetName((prev) => prev || preset.datasetName);
    setApiConfig((prev) => ({
      ...prev,
      functionName: prev.functionName || preset.functionName,
      description: prev.description || preset.description,
      url: prev.url || preset.url,
      method: preset.method || prev.method,
      parameters: prev.parameters.length > 0 ? prev.parameters : (preset.parameters || [])
    }));
    setPrefillNotice(`Prefilled from platform source preset: ${preset.datasetName}.`);
  }, [presetConfig]);

  const tabs = [
    { id: 'params', name: t('datasets.create.api.tabs.params', { defaultValue: 'Params' }), icon: DocumentTextIcon },
    { id: 'auth', name: t('datasets.create.api.tabs.authorization', { defaultValue: 'Authorization' }), icon: LockClosedIcon },
    { id: 'headers', name: t('datasets.create.api.tabs.headers', { defaultValue: 'Headers' }), icon: ListBulletIcon }
  ];

  const handleApiConfigChange = (field, value) => {
    setApiConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to generate function name from dataset name
  const generateFunctionName = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_{2,}/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  const handleDatasetNameChange = (value) => {
    setDatasetName(value);

    // Auto-generate function name only if it's empty or matches the previously generated name
    const currentGenerated = generateFunctionName(datasetName);
    if (!apiConfig.functionName || apiConfig.functionName === currentGenerated) {
      const newFunctionName = generateFunctionName(value);
      handleApiConfigChange('functionName', newFunctionName);
    }
  };

  const handleAuthChange = (field, value) => {
    setApiConfig(prev => ({
      ...prev,
      auth: {
        ...prev.auth,
        [field]: value
      }
    }));
  };

  const setVisibilityTarget = (target, enabled) => {
    setVisibilityTargets((prev) => {
      const hasTarget = prev.includes(target);
      if (enabled && !hasTarget) return [...prev, target];
      if (!enabled && hasTarget) return prev.filter((item) => item !== target);
      return prev;
    });
  };

  const addParameter = () => {
    setApiConfig(prev => ({
      ...prev,
      parameters: [...prev.parameters, { name: '', value: '', description: '', required: false }]
    }));
  };

  const removeParameter = (index) => {
    setApiConfig(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  const updateParameter = (index, field, value) => {
    setApiConfig(prev => ({
      ...prev,
      parameters: prev.parameters.map((param, i) =>
        i === index ? { ...param, [field]: value } : param
      )
    }));
  };

  const addHeader = () => {
    setApiConfig(prev => ({
      ...prev,
      headers: [...prev.headers, { name: '', value: '' }]
    }));
  };

  const removeHeader = (index) => {
    setApiConfig(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  const updateHeader = (index, field, value) => {
    setApiConfig(prev => ({
      ...prev,
      headers: prev.headers.map((header, i) =>
        i === index ? { ...header, [field]: value } : header
      )
    }));
  };

  const testApi = async () => {
    if (!apiConfig.url) {
      setTestError(t('datasets.create.api.errors.urlRequiredForTest', { defaultValue: 'URL is required to test the API' }));
      return;
    }

    setIsTesting(true);
    setTestError(null);
    setTestResponse(null);

    try {
      const result = await testDatasetExternalApi(apiConfig);

      if (result.success) {
        setTestResponse({
          status: result.status,
          statusText: result.statusText,
          headers: result.headers,
          data: result.data,
          time: Date.now(),
          message: t('datasets.create.api.test.successMessage', { defaultValue: 'API test successful via secure proxy' })
        });
      } else {
        setTestError({
          message: result.message,
          status: result.status,
          error: result.error,
          suggestion: t('datasets.create.api.test.failureSuggestion', { defaultValue: 'Check your URL and authentication settings. The request was proxied through our secure backend.' })
        });
      }
    } catch (error) {
      console.error('API test failed:', error);
      setTestError({
        message: t('datasets.create.api.test.unexpectedError', { defaultValue: 'An unexpected error occurred during testing.' }),
        suggestion: t('datasets.create.api.test.unexpectedSuggestion', { defaultValue: 'Please try again or check your internet connection.' })
      });
    } finally {
      setIsTesting(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!datasetName.trim()) {
      errors.datasetName = t('datasets.create.api.errors.datasetNameRequired', { defaultValue: 'Dataset name is required' });
    }

    if (!apiConfig.functionName.trim()) {
      errors.functionName = t('datasets.create.api.errors.functionNameRequired', { defaultValue: 'Function name is required' });
    }
    if (!apiConfig.description.trim()) {
      errors.description = t('datasets.create.api.errors.descriptionRequired', { defaultValue: 'Description is required' });
    }
    if (!apiConfig.url.trim()) {
      errors.url = t('datasets.create.api.errors.urlRequired', { defaultValue: 'URL is required' });
    }
    if (!isPresetLocked && visibilityTargets.length === 0) {
      errors.visibilityTargets = t('datasets.create.shared.errors.visibilityRequired', { defaultValue: 'Select at least one availability target' });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setError('');

    try {
      // Split URL back to baseUrl and endpoint for storage
      let baseUrl = apiConfig.url;
      let endpoint = '';

      try {
        const urlObj = new URL(apiConfig.url);
        baseUrl = `${urlObj.protocol}//${urlObj.host}`;
        endpoint = urlObj.pathname + urlObj.search;
      } catch (e) {
        // If URL parsing fails, store as is
      }

      const configToStore = {
        ...(isPresetLocked ? presetConfig : apiConfig),
        baseUrl,
        endpoint,
        // Keep auth data as is - no more hiding credentials
        auth: {
          ...(isPresetLocked ? presetConfig.auth || { type: 'none' } : apiConfig.auth)
        }
      };

      if (isPresetLocked) {
        configToStore.enabled = Boolean(presetEnabled);
      }

      // Get workspace ID (from localStorage or API)
      let workspaceId = sessionWorkspaceId;
      if (!workspaceId) {
        const refreshedContext = await refreshContext();
        workspaceId = refreshedContext.workspaceId;
      }
      if (!workspaceId) {
        throw new Error('Unable to determine workspace ID');
      }

      const datasetData = {
        name: (isPresetLocked ? presetConfig.datasetName : datasetName).trim(),
        status: 'published',
        type: 'external_api',
        api_config: configToStore, // Send as object, not JSON string
        dataset_type: 'external_api',
        linked_workspace: workspaceId,
        data_sets: isPresetLocked ? ['both'] : visibilityTargets
        // user_created will be set automatically by Directus based on the Bearer token
      };

      await createDataset(token, datasetData);
      navigate('/datasets');

    } catch (error) {
      if (window.location.hostname === 'localhost') {
        console.error('Error:', error.response?.data || error);
      }
      setError('Failed to create dataset: ' + (error.response?.data?.errors?.[0]?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'params':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">
                {t('datasets.create.api.params.title', { defaultValue: 'Query Parameters' })}
              </h4>
            </div>

            {apiConfig.parameters.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">{t('datasets.create.api.params.empty', { defaultValue: 'No parameters added yet' })}</p>
                <p className="text-xs mt-1">
                  {t('datasets.create.api.params.emptyHint', { defaultValue: 'Click "Add Parameter" to get started' })}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <div className="col-span-3">Key</div>
                  <div className="col-span-3">Value</div>
                  <div className="col-span-4">Description</div>
                  <div className="col-span-1">Required</div>
                  <div className="col-span-1"></div>
                </div>
                {apiConfig.parameters.map((param, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-3">
                      <Input
                        type="text"
                        placeholder="Parameter key"
                        value={param.name}
                        onChange={(e) => updateParameter(index, 'name', e.target.value)}
                        disabled={isLoading}
                        className="py-2 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="text"
                        placeholder="Parameter value"
                        value={param.value}
                        onChange={(e) => updateParameter(index, 'value', e.target.value)}
                        disabled={isLoading}
                        className="py-2 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="col-span-4">
                      <Input
                        type="text"
                        placeholder="Description (optional)"
                        value={param.description}
                        onChange={(e) => updateParameter(index, 'description', e.target.value)}
                        disabled={isLoading}
                        className="py-2 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <SwitchToggle
                        checked={param.required}
                        onChange={(checked) => updateParameter(index, 'required', checked)}
                        label={`Required ${param.name || index + 1}`}
                        className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => removeParameter(index)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-800 text-lg font-bold disabled:text-gray-400"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center pt-4">
              <Button
                type="button"
                onClick={addParameter}
                variant="secondary"
                size="md"
                disabled={isLoading}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('datasets.create.api.params.add', { defaultValue: 'Add Parameter' })}
              </Button>
            </div>
          </div>
        );

      case 'auth':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('datasets.create.api.auth.typeLabel', { defaultValue: 'Authorization Type' })}
              </label>
              <Select
                value={apiConfig.auth.type}
                onChange={(e) => handleAuthChange('type', e.target.value)}
                disabled={isLoading}
                className="form-select disabled:bg-gray-100"
              >
                <option value="none">{t('datasets.create.api.auth.none', { defaultValue: 'No Authorization' })}</option>
                <option value="bearer">{t('datasets.create.api.auth.bearer', { defaultValue: 'Bearer Token' })}</option>
                <option value="basic">Basic Auth</option>
                <option value="api-key">{t('datasets.create.api.auth.apiKey', { defaultValue: 'API Key' })}</option>
              </Select>
            </div>

            {apiConfig.auth.type === 'bearer' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('datasets.create.api.auth.bearerTokenLabel', { defaultValue: 'Bearer Token' })}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowSecrets(!showSecrets)}
                    className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                  >
                    {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                    {showSecrets
                      ? t('datasets.create.api.auth.hide', { defaultValue: 'Hide' })
                      : t('datasets.create.api.auth.show', { defaultValue: 'Show' })}
                  </button>
                </div>
                <Input
                  type={showSecrets ? 'text' : 'password'}
                  placeholder="Enter bearer token"
                  value={apiConfig.auth.token}
                  onChange={(e) => handleAuthChange('token', e.target.value)}
                  disabled={isLoading}
                  className="disabled:bg-gray-100"
                />
              </div>
            )}

            {apiConfig.auth.type === 'basic' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('datasets.create.api.auth.username', { defaultValue: 'Username' })}
                  </label>
                  <Input
                    type="text"
                    placeholder="Username"
                    value={apiConfig.auth.username}
                    onChange={(e) => handleAuthChange('username', e.target.value)}
                    disabled={isLoading}
                    className="disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('datasets.create.api.auth.password', { defaultValue: 'Password' })}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                    >
                      {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                      {showSecrets
                        ? t('datasets.create.api.auth.hide', { defaultValue: 'Hide' })
                        : t('datasets.create.api.auth.show', { defaultValue: 'Show' })}
                    </button>
                  </div>
                  <Input
                    type={showSecrets ? 'text' : 'password'}
                    placeholder="Password"
                    value={apiConfig.auth.password}
                    onChange={(e) => handleAuthChange('password', e.target.value)}
                    disabled={isLoading}
                    className="disabled:bg-gray-100"
                  />
                </div>
              </div>
            )}

            {apiConfig.auth.type === 'api-key' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('datasets.create.api.auth.headerName', { defaultValue: 'Header Name' })}
                  </label>
                  <Input
                    type="text"
                    placeholder="X-API-Key"
                    value={apiConfig.auth.apiKeyHeader}
                    onChange={(e) => handleAuthChange('apiKeyHeader', e.target.value)}
                    disabled={isLoading}
                    className="disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('datasets.create.api.auth.apiKeyValue', { defaultValue: 'API Key' })}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                    >
                      {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                      {showSecrets ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <Input
                    type={showSecrets ? 'text' : 'password'}
                    placeholder="Enter API key"
                    value={apiConfig.auth.apiKey}
                    onChange={(e) => handleAuthChange('apiKey', e.target.value)}
                    disabled={isLoading}
                    className="disabled:bg-gray-100"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'headers':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">Custom Headers</h4>
            </div>

            {apiConfig.headers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No custom headers added yet</p>
                <p className="text-xs mt-1">Authorization headers are managed in the Authorization tab</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-5 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <div className="col-span-2">Header Name</div>
                  <div className="col-span-2">Header Value</div>
                  <div className="col-span-1"></div>
                </div>
                {apiConfig.headers.map((header, index) => (
                  <div key={index} className="grid grid-cols-5 gap-3 items-center">
                    <div className="col-span-2">
                      <Input
                        type="text"
                        placeholder="Content-Type"
                        value={header.name}
                        onChange={(e) => updateHeader(index, 'name', e.target.value)}
                        disabled={isLoading}
                        className="py-2 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="text"
                        placeholder="application/json"
                        value={header.value}
                        onChange={(e) => updateHeader(index, 'value', e.target.value)}
                        disabled={isLoading}
                        className="py-2 disabled:bg-gray-100"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => removeHeader(index)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-800 text-lg font-bold disabled:text-gray-400"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={addHeader}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                disabled={isLoading}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Header
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderResponse = () => {
    if (!testResponse && !testError) return null;

    return (
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Response</h3>

        {testResponse && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-green-700">
                  {testResponse.status} {testResponse.statusText}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {testResponse.message || 'Response received'}
              </span>
              {testResponse.usedProxy && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  via proxy
                </span>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Response Body</h4>
                <span className="text-xs text-gray-500">
                  {typeof testResponse.data === 'object' ? 'JSON' : 'Text'}
                </span>
              </div>
              <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto max-h-64">
                {typeof testResponse.data === 'object'
                  ? JSON.stringify(testResponse.data, null, 2)
                  : testResponse.data
                }
              </pre>
            </div>
          </div>
        )}

        {testError && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-red-700">
                {testError.status ? `${testError.status} ${testError.statusText}` : 'Request Failed'}
              </span>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-red-700">Error Details</h4>
              </div>
              <div className="text-sm text-red-800">
                <p className="font-medium">{testError.message}</p>
                {testError.suggestion && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Note:</strong> {testError.suggestion}
                    </p>
                    {(testError.message?.toLowerCase().includes('cors') ||
                      testError.message?.toLowerCase().includes('network error') ||
                      testError.code === 'ERR_NETWORK') && (
                        <div className="mt-2 text-xs text-blue-700">
                          <p><strong>What this means:</strong></p>
                          <ul className="list-disc list-inside mt-1 space-y-1">
                            <li>The external API has CORS restrictions for browser requests</li>
                            <li>This is normal for many APIs when called from web browsers</li>
                            <li>Your AI assistant will call this API from the server, bypassing CORS</li>
                            <li>You can safely proceed with creating this dataset</li>
                          </ul>
                        </div>
                      )}
                  </div>
                )}
                {testError.data && (
                  <pre className="mt-2 whitespace-pre-wrap overflow-auto max-h-32">
                    {typeof testError.data === 'object'
                      ? JSON.stringify(testError.data, null, 2)
                      : testError.data
                    }
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to check if tabs have data
  const getTabIndicators = () => {
    return {
      params: apiConfig.parameters.length > 0,
      auth: apiConfig.auth.type !== 'none',
      headers: apiConfig.headers.length > 0
    };
  };

  return (
    <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
      <div className="px-4 py-8 sm:p-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {prefillNotice && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                {prefillNotice}
              </div>
            )}
            {/* Dataset Name */}
            <div>
              <label htmlFor="dataset-name" className="block text-sm font-medium leading-6 text-gray-900">
                Dataset Name
              </label>
              <div className="mt-2">
                <Input
                  type="text"
                  placeholder="i.e. Weather API Dataset"
                  id="dataset-name"
                  value={datasetName}
                  onChange={(e) => handleDatasetNameChange(e.target.value)}
                  disabled={isLoading || isPresetLocked}
                  required
                  error={Boolean(formErrors.datasetName)}
                  className={`disabled:bg-gray-100 ${isPresetLocked ? 'cursor-not-allowed' : ''}`}
                />
              </div>
              {formErrors.datasetName && (
                <p className="mt-2 text-sm text-red-600">{formErrors.datasetName}</p>
              )}
            </div>

            {isPresetLocked && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Status integracji</label>
                <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-gray-900">Włącz integrację</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Parametry tej integracji są zarządzane centralnie w Directus i nie podlegają edycji przez użytkownika.
                    </p>
                  </div>
                  <SwitchToggle
                    checked={presetEnabled}
                    onChange={(checked) => !isLoading && setPresetEnabled(checked)}
                    label="Włącz integrację"
                    className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                  />
                </div>
              </div>
            )}

            {/* Function Details */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="function-name" className="block text-sm font-medium text-gray-700">
                  Function Name
                </label>
                <Input
                  type="text"
                  id="function-name"
                  placeholder="e.g., search_weather_data"
                  value={apiConfig.functionName}
                  onChange={(e) => handleApiConfigChange('functionName', e.target.value)}
                  disabled={isLoading || isPresetLocked}
                  error={Boolean(formErrors.functionName)}
                  className="mt-1 disabled:bg-gray-100"
                />
                {formErrors.functionName && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.functionName}</p>
                )}
              </div>

              <div>
                <label htmlFor="response-format" className="block text-sm font-medium text-gray-700">
                  Response Format
                </label>
                <Select
                  id="response-format"
                  value={apiConfig.responseFormat}
                  onChange={(e) => handleApiConfigChange('responseFormat', e.target.value)}
                  disabled={isLoading || isPresetLocked}
                  className="mt-1 form-select disabled:bg-gray-100"
                >
                  <option value="json">JSON</option>
                  <option value="xml">XML</option>
                  <option value="text">Text</option>
                  <option value="csv">CSV</option>
                </Select>
              </div>
            </div>

            {!isPresetLocked && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('datasets.create.shared.availableIn', { defaultValue: 'Available in' })}
              </label>
              <div className="mt-3 space-y-3">
                <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-gray-900">
                      {t('datasets.create.shared.visibility.chat.title', { defaultValue: 'Chat / Assistant' })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('datasets.create.shared.visibility.chat.description', {
                        defaultValue: 'Enable if you want this dataset to be visible and used in Assistant.'
                      })}
                    </p>
                  </div>
                  <SwitchToggle
                    checked={visibilityTargets.includes('chat')}
                    onChange={(checked) => !isLoading && setVisibilityTarget('chat', checked)}
                    label={t('datasets.create.shared.visibility.chat.toggleAria', { defaultValue: 'Chat visibility' })}
                    className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                  />
                </div>
                <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-gray-900">
                      {t('datasets.create.shared.visibility.articles.title', { defaultValue: 'Articles' })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('datasets.create.shared.visibility.articles.description', {
                        defaultValue: 'Enable if you want this dataset used while generating articles.'
                      })}
                    </p>
                  </div>
                  <SwitchToggle
                    checked={visibilityTargets.includes('articles')}
                    onChange={(checked) => !isLoading && setVisibilityTarget('articles', checked)}
                    label={t('datasets.create.shared.visibility.articles.toggleAria', { defaultValue: 'Articles visibility' })}
                    className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                  />
                </div>
              </div>
              {formErrors.visibilityTargets && (
                <p className="mt-1 text-sm text-red-600">{formErrors.visibilityTargets}</p>
              )}
            </div>
            )}

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <TextArea
                id="description"
                rows={3}
                placeholder="Describe what this API function does..."
                value={apiConfig.description}
                onChange={(e) => handleApiConfigChange('description', e.target.value)}
                disabled={isLoading || isPresetLocked}
                error={Boolean(formErrors.description)}
                className="mt-1 disabled:bg-gray-100"
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
              )}
            </div>

            {/* Function Call Preview */}
            {apiConfig.functionName && (
              <Banner
                title="AI Assistant Function Preview"
                description="Preview how this endpoint will be exposed to the assistant."
                className="border-blue-200"
              >
                <div className="space-y-2">
                  <div>
                    <strong>Function Name:</strong>
                    <code className="bg-blue-100 px-2 py-1 rounded text-xs ml-2">{apiConfig.functionName}</code>
                  </div>
                  <div>
                    <strong>Description:</strong> {apiConfig.description || `Call ${datasetName} API endpoint`}
                  </div>
                  {apiConfig.parameters.length > 0 && (
                    <div>
                      <strong>Parameters:</strong>
                      <span className="ml-2">
                        {apiConfig.parameters.map(param => param.name).join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="bg-blue-100 p-3 rounded text-xs mt-3">
                    <strong>💡 How it works:</strong> Once created, this API will be automatically available to the AI assistant.
                    Users can ask questions that require this data, and the assistant will call your API in real-time to provide
                    up-to-date information in conversations.
                  </div>
                </div>
              </Banner>
            )}

            {!isPresetLocked && (
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('datasets.create.api.requestConfiguration', { defaultValue: 'API Request Configuration' })}
              </h3>

              {/* URL Input with Method */}
              <div className="flex gap-2 mb-6">
                <Select
                  value={apiConfig.method}
                  onChange={(e) => handleApiConfigChange('method', e.target.value)}
                  disabled={isLoading}
                  className="form-select text-sm font-medium max-w-24 disabled:bg-gray-100"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </Select>
                <Input
                  type="url"
                  placeholder="https://api.example.com/v1/endpoint"
                  value={apiConfig.url}
                  onChange={(e) => handleApiConfigChange('url', e.target.value)}
                  disabled={isLoading}
                  error={Boolean(formErrors.url)}
                  className="flex-1 w-full disabled:bg-gray-100"
                />
                <Button
                  type="button"
                  onClick={testApi}
                  disabled={isTesting || !apiConfig.url || isLoading}
                  variant="primary"
                  size="md"
                >
                  {isTesting ? (
                    <>
                      <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Testing...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4 mr-2" />
                      {t('datasets.create.api.send', { defaultValue: 'Send' })}
                    </>
                  )}
                </Button>
              </div>
              {formErrors.url && (
                <p className="mt-1 text-sm text-red-600 mb-4">{formErrors.url}</p>
              )}

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => {
                    const IconComponent = tab.icon;
                    const tabIndicators = getTabIndicators();
                    const hasData = tabIndicators[tab.id];

                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        disabled={isLoading}
                        className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap disabled:cursor-not-allowed flex items-center relative ${activeTab === tab.id
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                      >
                        <IconComponent className="h-4 w-4 mr-2" />
                        {tab.name}
                        {hasData && (
                          <div className="ml-2 h-2 w-2 bg-green-500 rounded-full"></div>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="py-6">
                {renderTabContent()}
              </div>
            </div>
            )}

            {/* Response Section */}
            {!isPresetLocked && renderResponse()}
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-x-6">

            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              size="md"
              className="disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                t('datasets.create.api.submit', { defaultValue: 'Create API Dataset' })
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateApiDataset; 